import { useCallback, useState } from "react";
import { useTaskMonitor } from "../../organisms/Provider/TaskProvider";
import { useNotification } from "../../../hooks/useNotification";
import { useXeroConnected } from "../../../hooks/useXeroConnected";
import {
  usePushCustomersToXeroMutation,
  usePushSuppliersToXeroMutation,
} from "../../../store/apiSlice/xeroContactApiSlice";

const MODULE_KIND = {
  customer: "customer",
  supplier: "supplier",
};

export const MasterPushToXeroButton = ({ module, recordId, xeroContactId }) => {
  const xeroConnected = useXeroConnected();
  const { startMonitoring } = useTaskMonitor();
  const notify = useNotification();
  const [pushCustomers, { isLoading: pushingCustomers }] = usePushCustomersToXeroMutation();
  const [pushSuppliers, { isLoading: pushingSuppliers }] = usePushSuppliersToXeroMutation();

  const kind = MODULE_KIND[module];
  const isLoading = pushingCustomers || pushingSuppliers;

  const handlePush = useCallback(async () => {
    if (!recordId || !kind) return;
    try {
      const push = kind === "customer" ? pushCustomers : pushSuppliers;
      const res = await push({ ids: [recordId] }).unwrap();
      if (res?.task_id) {
        startMonitoring(res.task_id);
        notify({ type: "success", message: "Pushing to Xero. Track progress in the task panel." });
      }
    } catch (error) {
      const message =
        error?.data?.detail ||
        error?.data?.ids?.[0] ||
        error?.data?.non_field_errors?.[0] ||
        "Failed to start push to Xero.";
      notify({ type: "error", message });
    }
  }, [kind, notify, pushCustomers, pushSuppliers, recordId, startMonitoring]);

  if (!xeroConnected || !kind || !recordId) {
    return null;
  }

  if (xeroContactId) {
    return (
      <span className="tableButton" style={{ cursor: "default", opacity: 0.85 }}>
        Linked
      </span>
    );
  }

  return (
    <button
      type="button"
      className="tableButton activeState"
      disabled={isLoading}
      onClick={handlePush}
    >
      {isLoading ? "Pushing…" : "Push to Xero"}
    </button>
  );
};

export const PushToXeroListAction = ({ orderType, selectedOrderId }) => {
  const xeroConnected = useXeroConnected();
  const { startMonitoring } = useTaskMonitor();
  const notify = useNotification();
  const [pushCustomers, { isLoading: pushingCustomers }] = usePushCustomersToXeroMutation();
  const [pushSuppliers, { isLoading: pushingSuppliers }] = usePushSuppliersToXeroMutation();
  const [busy, setBusy] = useState(false);

  const kind = MODULE_KIND[orderType];
  const hasSelection = selectedOrderId?.length > 0;
  const isLoading = busy || pushingCustomers || pushingSuppliers;

  const handlePush = async () => {
    if (!hasSelection || !kind) return;
    setBusy(true);
    try {
      const push = kind === "customer" ? pushCustomers : pushSuppliers;
      const res = await push({ ids: selectedOrderId }).unwrap();
      if (res?.task_id) {
        startMonitoring(res.task_id);
        notify({ type: "success", message: "Pushing to Xero. Track progress in the task panel." });
      }
    } catch (error) {
      const message =
        error?.data?.detail ||
        error?.data?.ids?.[0] ||
        "Failed to start push to Xero.";
      notify({ type: "error", message });
    } finally {
      setBusy(false);
    }
  };

  if (!xeroConnected || !kind) {
    return null;
  }

  return (
    <li>
      <button
        className={`tableButton ${hasSelection ? "activeState" : ""}`}
        disabled={!hasSelection || isLoading}
        title={hasSelection ? undefined : `Select one or more ${kind}s to push to Xero.`}
        onClick={handlePush}
      >
        {isLoading ? "Pushing…" : "Push to Xero"}
      </button>
    </li>
  );
};
